/**
 * Brand Status Monitor Component
 * Monitors brand status and handles deactivation notifications
 */

import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBrand } from '../context/BrandContext';
import { useToast } from '../context/ToastContext';

const getApiUrl = () => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  if (process.env.NODE_ENV === 'production') return 'https://yemberzal-app-28481d1d0b39.herokuapp.com';
  return 'http://localhost:8000';
};

const API_URL = getApiUrl();

function BrandStatusMonitor({ children }) {
  const navigate = useNavigate();
  const { isAuthenticated, brand, logout, accessToken } = useBrand();
  const { warning } = useToast();
  const hasLoggedOut = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || !brand || !accessToken) return;
    hasLoggedOut.current = false;

    const checkBrandStatus = async () => {
      if (hasLoggedOut.current) return;

      try {
        // Use direct fetch to bypass authFetch's 401 interception
        const response = await fetch(`${API_URL}/api/brand/status-check/`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
        });

        if (response.status === 403) {
          // Brand was deactivated — backend returned explicit 403
          const data = await response.json();
          hasLoggedOut.current = true;
          logout();
          warning(data.message || 'Your brand account has been deactivated. Please contact the admin to reactivate your account.');
          navigate('/brand-portal/login');
        } else if (response.status === 401) {
          // Auth failed entirely — brand may have been deactivated
          // and the token can no longer authenticate
          hasLoggedOut.current = true;
          logout();
          warning('Your session has ended. Your brand account may have been deactivated. Please contact the admin if you cannot log in.');
          navigate('/brand-portal/login');
        }
      } catch (err) {
        console.error('Status check failed:', err);
      }
    };

    // Check immediately, then every 30 seconds
    checkBrandStatus();
    const statusInterval = setInterval(checkBrandStatus, 30000);

    return () => clearInterval(statusInterval);
  }, [isAuthenticated, brand, accessToken, logout, navigate]);

  return children;
}

export default BrandStatusMonitor;
